import {
  entityLocalized,
  entityName,
  type ModerationEntity,
  type Translate,
} from '@/entities/moderation';
import { LOCALE_ORDER, localeLine } from '../../lib/locales';

interface LocalizedSectionProps {
  own: ModerationEntity;
  other: ModerationEntity | null | undefined;
  highlight: boolean;
  t: Translate;
}

export function NameSection({ own, other, highlight, t }: LocalizedSectionProps) {
  const ownName = entityName(own);
  const otherName = other ? entityName(other) : undefined;

  // All three locales always render (empty reads as `—`) — the reviewer
  // sees every field even when it is unchanged or unfilled.
  return (
    <div>
      <h4 className="text-[14px] leading-[normal] font-semibold tracking-[-0.28px] text-black dark:text-foreground">
        {t('detail.name', 'Название')}
      </h4>
      <dl className="mt-3 flex flex-col gap-3 text-sm leading-[1.4] font-normal">
        {LOCALE_ORDER.map(({ key, labelKey, fallback }) => {
          const value = localeLine(ownName, key);
          const changed = value !== localeLine(otherName, key);
          return (
            <div key={key} className="grid min-h-11 w-full grid-cols-2 gap-3">
              <dt className="font-normal text-[#9ca3af] dark:text-muted-foreground">
                {t(labelKey, fallback)}
              </dt>
              <dd
                className={
                  highlight && changed
                    ? 'min-w-0 font-normal break-words text-destructive'
                    : 'min-w-0 font-normal break-words text-foreground'
                }
              >
                {value === '' ? '—' : value}
              </dd>
            </div>
          );
        })}
      </dl>
    </div>
  );
}

export function DescriptionSection({ own, other, highlight, t }: LocalizedSectionProps) {
  const ownDesc = entityLocalized(own, 'description');
  const otherDesc = other ? entityLocalized(other, 'description') : undefined;

  // All three locales always render (empty reads as `—`) — the reviewer
  // sees every field even when it is unchanged or unfilled.
  return (
    <div>
      <h4 className="text-[14px] leading-[normal] font-semibold tracking-[-0.28px] text-black dark:text-foreground">
        {t('detail.description', 'Описание')}
      </h4>
      <div className="mt-3 flex flex-col gap-4">
        {LOCALE_ORDER.map(({ key, labelKey, fallback }) => {
          const value = localeLine(ownDesc, key);
          const changed = value !== localeLine(otherDesc, key);
          return (
            <div
              key={key}
              className="flex min-h-20 w-full flex-col gap-1.5 text-sm leading-[1.4] font-normal"
            >
              <p className="font-normal text-[#9ca3af] dark:text-muted-foreground">
                {t(labelKey, fallback)}
              </p>
              <p
                className={
                  highlight && changed
                    ? 'rounded-[16px] bg-destructive/10 p-3 text-sm leading-[normal] font-normal break-words text-destructive'
                    : 'rounded-[16px] bg-[#f3f4f6] p-3 text-sm leading-[normal] font-normal break-words text-[#0f172a] dark:bg-muted dark:text-foreground'
                }
              >
                {value === '' ? '—' : value}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
}
